import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { User, Package, MapPin, Plus, Pencil, Trash2, Star, LogOut } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/constants/branding";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import AddressDialog from "@/components/AddressDialog";

export default function Account() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const tab = params.get("tab") || "profile";
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadAddresses = () => api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});

  useEffect(() => {
    loadOrders();
    loadAddresses();
  }, []);

  const deleteAddress = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Address removed");
      loadAddresses();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setDefault = async (id) => {
    try {
      await api.post(`/addresses/${id}/default`);
      loadAddresses();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="shell max-w-5xl py-5 sm:py-7 animate-fade-up">
      <h1 className="font-display text-3xl font-bold text-deep mb-5">My Account</h1>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-6 h-11 rounded-xl bg-secondary p-1">
          <TabsTrigger value="profile" data-testid="account-tab-profile"><User className="h-4 w-4 mr-1.5" /> Profile</TabsTrigger>
          <TabsTrigger value="orders" data-testid="account-tab-orders"><Package className="h-4 w-4 mr-1.5" /> Orders</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="account-tab-addresses"><MapPin className="h-4 w-4 mr-1.5" /> Addresses</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="surface p-6 max-w-md">
            <div className="flex items-center gap-4 mb-5">
              <div className="h-14 w-14 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center">
                {(user.name || "U").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-lg">{user.name}</p>
                <p className="text-sm text-muted-foreground capitalize">{user.role}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <Field label="Email" value={user.email} />
              <Field label="Phone" value={user.phone || "—"} />
            </div>
            <Button variant="outline" className="mt-6 gap-2" onClick={() => { logout(); navigate("/"); }} data-testid="account-logout">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          {orders.length === 0 ? (
            <Empty icon={Package} title="No orders yet" desc="Your orders will appear here once you place them." action={<Button onClick={() => navigate("/products")}>Start Shopping</Button>} />
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="surface p-5" data-testid={`account-order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-border">
                    <div>
                      <p className="font-bold">{o.order_number}</p>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full capitalize">{o.status}</span>
                  </div>
                  <div className="py-3 space-y-2">
                    {o.items.map((it) => (
                      <div key={it.product_id} className="flex items-center gap-3 text-sm">
                        <span className="h-12 w-12 shrink-0 rounded-lg bg-secondary overflow-hidden"><img src={it.image} alt={it.name} className="h-full w-full object-contain p-1 mix-blend-multiply" /></span>
                        <span className="flex-1 truncate">{it.name} × {it.qty}</span>
                        <span className="font-medium">{formatINR(it.line_total)}</span>
                      </div>
                    ))}
                  </div>
                  {o.items.some((it) => it.tracking_id) && (
                    <p className="text-xs text-muted-foreground pb-3" data-testid={`order-tracking-${o.id}`}>
                      Shipped via <b>{o.items.find((it) => it.tracking_id).courier}</b> · Tracking ID <b>{o.items.find((it) => it.tracking_id).tracking_id}</b>
                    </p>
                  )}
                  <div className="pt-3 border-t border-border flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Payment: {o.payment_method.toUpperCase()}</span>
                    <span className="font-bold">Total: {formatINR(o.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="addresses">
          <div className="flex justify-end mb-4">
            <Button className="gap-1.5" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="account-add-address">
              <Plus className="h-4 w-4" /> Add Address
            </Button>
          </div>
          {addresses.length === 0 ? (
            <Empty icon={MapPin} title="No saved addresses" desc="Add a delivery address to speed up checkout." action={<Button onClick={() => { setEditing(null); setDialogOpen(true); }}>Add delivery address</Button>} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {addresses.map((a) => (
                <div key={a.id} className="surface p-5" data-testid={`account-address-${a.id}`}>
                  <div className="flex items-start justify-between">
                    <p className="font-semibold">{a.name}</p>
                    {a.is_default && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded flex items-center gap-1"><Star className="h-3 w-3" /> Default</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{a.phone}</p>
                  <p className="text-sm text-muted-foreground">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} - {a.pincode}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`address-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    {!a.is_default && <Button size="sm" variant="ghost" onClick={() => setDefault(a.id)} data-testid={`address-default-${a.id}`}>Set Default</Button>}
                    <Button size="sm" variant="ghost" className="text-destructive gap-1" onClick={() => deleteAddress(a.id)} data-testid={`address-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddressDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={loadAddresses} />
    </div>
  );
}

const Field = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const Empty = ({ icon: Icon, title, desc, action }) => (
  <div className="text-center py-16">
    <Icon className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
    <p className="font-semibold text-lg">{title}</p>
    <p className="text-muted-foreground text-sm mt-1 mb-5">{desc}</p>
    {action}
  </div>
);
